import { FC, useState, useEffect } from 'react';
import { UploadedFile } from '@/types/index';
import { fileService } from '@services/file-service';
import { PhotoUploadZone } from '../PhotoUploadZone';
import { PhotoGallery } from '../PhotoGallery';

interface Step6PhotosProps {
  propertyId?: string;  // Peut être undefined si nouvelle propriété
}

export const Step6Photos: FC<Step6PhotosProps> = ({ propertyId }) => {
  const [photos, setPhotos] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (propertyId) {
      loadPhotos();
    }
  }, [propertyId]);

  const loadPhotos = async () => {
    if (!propertyId) return;

    try {
      setLoading(true);
      const files = await fileService.listPropertyFiles(propertyId);
      setPhotos(files.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }));
    } catch (error) {
      console.error('Erreur chargement photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (file: UploadedFile) => {
    setPhotos((prev) => {
      const updated = [...prev, file];
      return updated.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    });
  };

  const handleSetPrimary = async (fileId: string) => {
    const updatedFile = await fileService.setPrimaryPhoto(fileId);
    setPhotos((prev) =>
      prev.map((p) => ({
        ...p,
        isPrimary: p.id === updatedFile.id,
      })).sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      })
    );
  };

  const handleDelete = async (fileId: string) => {
    await fileService.deleteFile(fileId);
    setPhotos((prev) => prev.filter((p) => p.id !== fileId));
  };

  if (!propertyId) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Photos
        </h2>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            💡 Veuillez d'abord enregistrer la propriété avant d'ajouter des photos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Photos ({photos.length}/10)
      </h2>

      {/* Zone d'upload */}
      {photos.length < 10 && (
        <PhotoUploadZone
          propertyId={propertyId}
          photos={photos}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Galerie */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Chargement des photos...</p>
        </div>
      ) : (
        <PhotoGallery
          photos={photos}
          onSetPrimary={handleSetPrimary}
          onDelete={handleDelete}
        />
      )}

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
          📸 Conseils pour les photos
        </h3>
        <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
          <li>• Formats acceptés : JPEG, PNG, WebP</li>
          <li>• Maximum 10 photos par propriété</li>
          <li>• La première photo est définie comme photo principale par défaut</li>
          <li>• Cliquez sur ⭐ pour changer la photo principale</li>
          <li>• La photo principale s'affiche dans la liste des propriétés</li>
        </ul>
      </div>
    </div>
  );
};
